import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import { Card, Space, Steps, Tag, Typography } from 'antd';
import React, { useEffect, useRef } from 'react';
import { TwilioOnboardingStatus } from '../../types/twilio-onboarding';
import { fetchOnboardingStatus } from '../../utils/twilio-onboarding-api';
import {
  getStepDisplayStatus,
  resolveCurrentStepIndex,
  shouldPollStatus,
  STATUS_STEPS,
} from './onboarding-logic';

const { Text } = Typography;

const SidRow: React.FC<{ label: string; value?: string }> = ({
  label,
  value,
}) => {
  if (!value) {
    return null;
  }
  return (
    <div style={{ marginBottom: 4 }}>
      <Text type="secondary">{label}: </Text>
      <Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
        {value}
      </Text>
    </div>
  );
};

interface OnboardingStatusTrackerProps {
  apiUrl: string;
  accessToken: string;
  clientId: string;
  status: TwilioOnboardingStatus;
  onStatusUpdate: (status: TwilioOnboardingStatus) => void;
}

export const OnboardingStatusTracker: React.FC<
  OnboardingStatusTrackerProps
> = ({ apiUrl, accessToken, clientId, status, onStatusUpdate }) => {
  const actionInFlightRef = useRef(false);
  const onStatusUpdateRef = useRef(onStatusUpdate);
  onStatusUpdateRef.current = onStatusUpdate;

  useEffect(() => {
    if (!shouldPollStatus(status)) {
      return;
    }

    const interval = setInterval(async () => {
      if (actionInFlightRef.current) {
        return;
      }
      try {
        const updated = await fetchOnboardingStatus(
          apiUrl,
          accessToken,
          clientId,
        );
        if (updated) {
          onStatusUpdateRef.current(updated);
        }
      } catch {
        // Silently ignore poll errors
      }
    }, 30_000);

    return () => clearInterval(interval);
  }, [apiUrl, accessToken, clientId, status.status]);

  const isRejected = status.status === 'rejected';
  const currentIndex = resolveCurrentStepIndex(status);

  const getStepIcon = (stepIndex: number) => {
    const stepStatus = getStepDisplayStatus(stepIndex, status);
    if (stepStatus === 'error') {
      return <CloseCircleFilled style={{ color: '#f5222d' }} />;
    }
    if (stepStatus === 'finish') {
      return <CheckCircleFilled style={{ color: '#52c41a' }} />;
    }
    if (stepStatus === 'process') {
      return <LoadingOutlined style={{ color: '#5b1eaf' }} />;
    }
    return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
  };

  return (
    <Card style={{ marginBottom: 24 }} title="Onboarding Status">
      <div style={{ display: 'flex', gap: 32 }}>
        <div style={{ flex: '0 0 280px' }}>
          <Steps current={currentIndex} direction="vertical" size="small">
            {STATUS_STEPS.map((step, i) => (
              <Steps.Step
                description={
                  isRejected && i === currentIndex && status.rejection_details
                    ? `Rejected: ${status.rejection_details.length > 80 ? status.rejection_details.substring(0, 80) + '...' : status.rejection_details}`
                    : undefined
                }
                icon={getStepIcon(i)}
                key={step.key}
                status={getStepDisplayStatus(i, status)}
                title={step.label}
              />
            ))}
          </Steps>
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Text strong>Current Status:</Text>
              {isRejected ? (
                <Tag color="red">Rejected at {status.rejection_step}</Tag>
              ) : status.status === 'active' ? (
                <Tag color="green">Active</Tag>
              ) : (
                <Tag color="processing">{status.status.replace(/_/g, ' ')}</Tag>
              )}
            </Space>
          </div>

          {status.outbound_phone_number && (
            <div
              style={{
                marginBottom: 16,
                padding: 16,
                background: '#f6ffed',
                borderRadius: 8,
              }}
            >
              <Text strong style={{ fontSize: 16 }}>
                Phone Number: {status.outbound_phone_number}
              </Text>
            </div>
          )}

          <div style={{ padding: 16, background: '#fafafa', borderRadius: 8 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Twilio Resource IDs
            </Text>
            <SidRow label="Profile SID" value={status.customer_profile_sid} />
            <SidRow label="A2P Profile SID" value={status.a2p_profile_sid} />
            <SidRow label="Brand SID" value={status.brand_registration_sid} />
            <SidRow label="Campaign SID" value={status.campaign_sid} />
            <SidRow
              label="Messaging Service SID"
              value={status.messaging_service_sid}
            />
            {!status.customer_profile_sid && !status.brand_registration_sid && (
              <Text type="secondary">
                SIDs will appear as each step completes.
              </Text>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
